import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col, Tooltip, Drawer, Grid} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchGroupsList, deleteGroup } from "../groupThunks";
import { App } from "antd";
import { setSelectedGroup, setIsEditGroup } from "../groupSlice";
import { selectGroupsList } from "../groupSelectors";
import { getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";

const GroupList = () => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const groupsList = useSelector(selectGroupsList);
  let [access_type, access_type_set] = useState("");
  const isAdminUser = useIsAdminUser();
   const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
  const [helpOpen, setHelpOpen] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });



  useEffect(() => {
    const access = getAccessType("group_entry");
    access_type_set(access);
  }, []);

  const handleDelete = async (group_id, group_name) => {
    try {
      await dispatch(deleteGroup(group_id)).unwrap();
      modal.success({
        title: "Group deleted successfully",
      });
      dispatch(fetchGroupsList());
    } catch (error) {
      modal.warning({
        title: error.message || "Delete failed",
      });
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const columns = [
    {
      title: "NO.",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Group Name",
      dataIndex: "group_name",
      sorter: (a, b) => a.group_name?.localeCompare(b.group_name),
    },
    ...(access_type === "WRITE"
      ? [
          {
            title: "Actions",
            render: (_, record) => (
              <>
                <Button
                  type="link"
                  disabled={!(isAdminUser || canEdit)}
                  onClick={() => {
                    dispatch(setSelectedGroup(record));
                    dispatch(setIsEditGroup(true));
                    navigate("addNewGroup");
                  }}
                  icon={<EditOutlined />}
                />
                <Button
                  type="link"
                  danger
                  disabled={!(isAdminUser || canDelete)}
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    modal.confirm({
                      title: "Are you sure?",
                      content: `This will delete group: ${record.group_name}`,
                      okText: "Yes, Delete",
                      cancelText: "Cancel",
                      onOk: () =>
                        handleDelete(record.group_id, record.group_name),
                    });
                  }}
                />
              </>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    dispatch(fetchGroupsList());
  }, [dispatch]);

  return (
    <>
      <Row justify="end" gutter={12} style={{ marginBottom: "32px", marginTop: "32px" }}>
        <Col>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </Col>
        <Col>
          {access_type == "WRITE" && (
            <>
              {" "}
              <Button
                type="primary"
                className="traic-button"
                icon={<PlusOutlined />}
                style={{
                  minWidth: "112px",
                  height: "36px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
                onClick={() => {
                  dispatch(setSelectedGroup(null));
                  dispatch(setIsEditGroup(false));
                  navigate("/inventory/item-group-entry/addNewGroup");
                }}
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={groupsList}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"]
        }}
        rowKey="group_id"
        scroll={{ x: "max-content" }}
      />
      <Drawer
        title="Group Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Item Group Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Item Group module is used to create and manage product groups
            within the ERP system. Groups help organize inventory items for
            easier management, filtering, and reporting.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View existing item groups</li>
              <li>Use <strong>Add New</strong> to create groups</li>
              <li>Edit group details when required</li>
              <li>Delete unused groups if permitted</li>
              <li>Organize inventory items into groups</li>
              <li>Manage item grouping for reporting purposes</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Group names should remain unique.</li>
              <li>Deleting groups may affect linked inventory items.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Groups improve inventory organization and reporting.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default GroupList;
